import type React from 'react';

import Typography from '@mui/material/Typography';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';

import StandardButton from 'components/buttons/StandardButton';
import { PenaltyExtensionNames, type PenaltyInterface } from 'typedefs/store/tmxTypes';

import { useSuspensionPointsStyles } from './styles';

interface PenaltyPointsCardProps {
  participantPenalty: PenaltyInterface;
  onEdit?: (participantPenalty: PenaltyInterface) => void;
  onRemove?: (participantPenalty: PenaltyInterface) => void;
}

const PenaltyPointsCard: React.FC<React.PropsWithChildren<PenaltyPointsCardProps>> = ({
  participantPenalty,
  onEdit,
  onRemove,
}) => {
  const { classes } = useSuspensionPointsStyles();
  const { t } = useTranslation();

  let shortDescription = t('descriptionNotAvailable');
  let longDescription = '';
  let points = 0;

  for (const extension of participantPenalty.extensions || []) {
    if (extension.name === PenaltyExtensionNames.LONG_DESCRIPTION) {
      longDescription = extension.value.toString();
    }

    if (extension.name === PenaltyExtensionNames.SHORT_DESCRIPTION) {
      shortDescription = extension.value.toString();
    }

    if (extension.name === PenaltyExtensionNames.POINTS) {
      points = Number(extension.value);
    }
  }

  const handleEditClick = () => {
    if (typeof onEdit === 'function') {
      onEdit(participantPenalty);
    }
  };

  const handleRemoveClick = () => {
    if (typeof onRemove === 'function') {
      onRemove(participantPenalty);
    }
  };

  return (
    <div className={classes.card}>
      <div className={classes.cardContent}>
        <Typography className={classes.cardTitle} component="h3">
          {participantPenalty.penaltyCode} - {shortDescription} {t('players.penaltyPointsLong', { count: points })}
        </Typography>
        {!!longDescription && <Typography variant="body2">{longDescription}</Typography>}

        {Boolean(participantPenalty.issuedAt) && (
          <div className={classes.cardDateWrapper}>
            <Typography className={classes.cardFieldLabel} component="span" variant="body2">
              {t('issuedDate')}:
            </Typography>
            <Typography component="span" variant="body2">
              {dayjs(participantPenalty.issuedAt).format('dddd Do MMM YYYY')}
            </Typography>
          </div>
        )}

        {Boolean(participantPenalty.notes) && (
          <div className={classes.cardCommentWrapper}>
            <Typography className={classes.cardFieldLabel} variant="body2">
              {t('comment')}:
            </Typography>
            <Typography variant="body2">{participantPenalty.notes}</Typography>
          </div>
        )}
      </div>
      <div className={classes.cardActions}>
        <StandardButton onClick={handleEditClick}>{t('Edit')}</StandardButton>
        <StandardButton onClick={handleRemoveClick}>{t('remove')}</StandardButton>
      </div>
    </div>
  );
};

export default PenaltyPointsCard;
